/**
 * Dashboard Permission Utilities
 *
 * Spec-aligned helpers for checking dashboard page access. Reads role/org
 * info from the new dual-storage shape via ./orgMembership and ./orgRoles.
 */

import {
  PERMISSIONS,
  ROLES,
  roleHasPermission,
} from './orgRoles';
import {
  getAllUserOrgs,
  getUserOrgRole,
  isOrgOwner,
  getUserOwnedOrgs,
  getUserMemberships,
} from './orgMembership';
import { getOrgSubscription } from './orgSubscription';

// ================ PAGE PERMISSION MAPPING ================ //

/**
 * Map of dashboard pages to required permission keys (from PERMISSIONS in
 * src/util/orgRoles.js). canAccessDashboardPage requires ALL listed perms.
 */
export const PAGE_PERMISSIONS = {
  // Team & Organization Management
  OrgSettingsTeamsPage: ['INVITE_TEAM'], // old MANAGE_TEAM_MEMBERS -> INVITE_TEAM
  OrgSettingsAssignmentsPage: ['VIEW_DASHBOARD'], // Members can read; writes gated server-side by ASSIGN_CREATORS
  OrgSettingsProfilePage: ['VIEW_DASHBOARD'], // old EDIT_ORGANIZATION_PROFILE -> VIEW_DASHBOARD (no direct equivalent)

  // Analytics & Revenue
  OrgInsightsPage: ['VIEW_INSIGHTS'], // old VIEW_REVENUE_TRANSACTIONS dropped -> VIEW_INSIGHTS only
  RevenueAnalyticsPage: ['VIEW_INSIGHTS'], // old VIEW_REVENUE_TRANSACTIONS -> VIEW_INSIGHTS

  // Content Management
  ManageListingsPage: ['CREATE_SHOWS'], // old CREATE_EDIT_SHOWS/CREATE_EDIT_LIVE_ROOMS -> CREATE_SHOWS
  EditListingPage: ['EDIT_ANY_SHOW'], // old CREATE_EDIT_SHOWS/CREATE_EDIT_LIVE_ROOMS -> EDIT_ANY_SHOW
  ManageSubscriptionsPage: ['MANAGE_SUBSCRIPTION'], // old CREATE_EDIT_LIVE_ROOMS -> MANAGE_SUBSCRIPTION

  // Member Management
  MembersPage: ['VIEW_DASHBOARD'], // old ACCESS_VIEW_MEMBER_DASHBOARD -> VIEW_DASHBOARD

  // Communication
  EmailCampaignsPage: ['SEND_NEWSLETTERS'], // old SEND_EMAIL_CAMPAIGNS -> SEND_NEWSLETTERS
  MessageInboxPage: ['VIEW_DASHBOARD'], // old ACCESS_MESSAGE_INBOX -> VIEW_DASHBOARD

  // Perks & Marketing
  ManagePerksPage: ['SEND_NEWSLETTERS'], // old MANAGE_PERKS -> SEND_NEWSLETTERS

  // Billing (owner only)
  BillingPage: ['VIEW_BILLING'], // old MANAGE_TEAM_BILLING -> VIEW_BILLING
  PaymentHistoryPage: ['VIEW_BILLING'], // old MANAGE_TEAM_BILLING -> VIEW_BILLING
  UpgradePage: ['MANAGE_SUBSCRIPTION'], // old MANAGE_TEAM_BILLING -> MANAGE_SUBSCRIPTION
};

// ================ MAIN PERMISSION FUNCTIONS ================ //

/**
 * Check if user can access a specific dashboard page.
 *
 * @param {object} user - User object.
 * @param {string} organizationId - Organization ID.
 * @param {string} pageName - Dashboard page name.
 * @param {object} context - Additional context (unused; reserved for future).
 * @returns {object} { canAccess: boolean, reason?: string }
 */
// eslint-disable-next-line no-unused-vars
export const canAccessDashboardPage = (user, organizationId, pageName, context = {}) => {
  if (!user || !organizationId || !pageName) {
    return { canAccess: false, reason: 'Missing required parameters' };
  }

  const role = getUserOrgRole(user, organizationId);
  if (!role) {
    return { canAccess: false, reason: 'Not a member of this organization' };
  }

  const requiredPermissions = PAGE_PERMISSIONS[pageName];
  if (requiredPermissions && requiredPermissions.length > 0) {
    for (const permission of requiredPermissions) {
      if (!roleHasPermission(role, permission)) {
        return {
          canAccess: false,
          reason: `Missing required permission: ${permission}`,
        };
      }
    }
  }

  return { canAccess: true };
};

/**
 * Get an org enriched with the user's role, isOwner flag, and the list of
 * permission keys their role has. If `organizationId` is not given, falls
 * back to the first owned/joined org.
 *
 * @param {object} user
 * @param {string|null} organizationId
 * @returns {object|null}
 */
export const getSelectedOrganizationWithPermissions = (user, organizationId = null) => {
  if (!user) return null;

  let targetOrgId = organizationId;
  if (!targetOrgId) {
    const owned = getUserOwnedOrgs(user);
    if (owned.length > 0 && owned[0]?.id) {
      targetOrgId = owned[0].id;
    } else {
      const memberships = getUserMemberships(user);
      if (memberships.length > 0 && memberships[0]?.orgId) {
        targetOrgId = memberships[0].orgId;
      }
    }
  }

  if (!targetOrgId) return null;

  const role = getUserOrgRole(user, targetOrgId);
  if (!role) return null;

  const isOwner = isOrgOwner(user, targetOrgId);

  // Find the org branding record.
  const allOrgs = getAllUserOrgs(user);
  const orgRecord = allOrgs.find(o => o.id === targetOrgId) || { id: targetOrgId };

  // Subscription is only readable for owners (privateData on owner's profile).
  const subscription = isOwner ? getOrgSubscription(user, targetOrgId) : null;

  // Permissions the role has.
  const permissions = Object.keys(PERMISSIONS).filter(p => roleHasPermission(role, p));

  return {
    ...orgRecord,
    id: targetOrgId,
    role,
    isOwner,
    subscription,
    permissions,
  };
};

/**
 * Does the user belong to any org at all?
 */
export const hasAnyOrganizationAccess = user => {
  if (!user) return false;
  return getAllUserOrgs(user).length > 0;
};

/**
 * Every org the user has access to, each enriched with role/isOwner/permissions.
 */
export const getAccessibleOrganizations = user => {
  if (!user) return [];

  return getAllUserOrgs(user).map(org => {
    const role = org.role || getUserOrgRole(user, org.id) || ROLES.MEMBER;
    const isOwner = !!org.isOwner;
    const permissions = Object.keys(PERMISSIONS).filter(p => roleHasPermission(role, p));
    return {
      ...org,
      role,
      isOwner,
      permissions,
    };
  });
};

export default {
  canAccessDashboardPage,
  getSelectedOrganizationWithPermissions,
  hasAnyOrganizationAccess,
  getAccessibleOrganizations,
  PAGE_PERMISSIONS,
};
